import { FC, memo, useMemo } from "react";
import { dayjs } from "utils";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import {
  getFreeFeatures,
  getCompleteFeatures,
} from "../shared/edCompleteFeatures";
import {
  getDisplayPlanName,
  getPayCyclePillLabel,
} from "../shared/planDisplayUtils";
import Translations from "@translate/components/Member/CurrentPlan/SubscriptionPlanCard";
import { I18nUtil } from "@utils/i18n";
import { MemberPlanInfo } from "@hooks/useMemberUser";
import { BillingCycleType } from "../Subscription/types";
import { useIsPc } from "@/src/hooks/useIsPc";
import { ProductPayCycle } from "./constants";
import { ManageBillingUpgrade } from "./ManageBillingUpgrade";

const I18n = I18nUtil.createScoped(Translations);

interface SubscriptionPlanCardProps {
  data?: MemberPlanInfo;
  toMemberPrice: () => void;
}

const FeatureRow = ({ text }: { text: string }) => (
  <div className="flex items-center gap-2">
    <div className="flex size-4 shrink-0 items-center justify-center">
      <Icon
        name="check"
        size={16}
        color={ospThemes.colors.content_brand_primary}
      />
    </div>
    <span className="body_medium_m text-content_secondary">{text}</span>
  </div>
);

export const SubscriptionPlanCard: FC<SubscriptionPlanCardProps> = memo(
  ({ data, toMemberPrice }) => {
    const [isPc] = useIsPc();
    const isFree = data?.isFree ?? true;
    const features = isFree
      ? getFreeFeatures()
      : getCompleteFeatures(data?.payCycle as BillingCycleType);
    const displayName = getDisplayPlanName(data?.level);
    const payCycleLabel = getPayCyclePillLabel(data?.payCycle, data?.isTrial);
    const renewalDate = data?.expire_time
      ? dayjs(data.expire_time).utc().format("MMM D, YYYY")
      : undefined;
    const isLifetime = data?.payCycle === ProductPayCycle.LIFETIME;

    const payCycleText = useMemo(() => {
      if (isLifetime) {
        return I18n.t("one-time");
      }
      let payCycle = data?.payCycle || "";
      if (data?.isTrial) {
        payCycle = "week";
      }
      return I18n.t("per %{var0}", {
        var0: payCycle.toLowerCase(),
      });
    }, [isLifetime, data?.payCycle, data?.isTrial]);

    const autoRenewalText = useMemo(() => {
      if (isLifetime) {
        return I18n.t("Off");
      }
      return data?.auto_renewal ? I18n.t("On") : I18n.t("Off");
    }, [isLifetime, data?.auto_renewal]);

    return (
      <div className="w-full max-w-[740px] rounded-3 bg-background_solid_neutral_primary p-6 flex flex-col gap-8">
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between gap-4">
            {isFree ? (
              <h2 className="headline_regular_m text-content_primary">
                {displayName}
              </h2>
            ) : (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="headline_regular_m text-content_primary">
                    {displayName}
                  </h2>
                  <span className="body_medium_s text-content_tertiary">
                    · {payCycleLabel}
                  </span>
                </div>
              </div>
            )}

            {isPc && (
              <ManageBillingUpgrade
                isFree={isFree}
                toMemberPrice={toMemberPrice}
                data={data}
              />
            )}
          </div>

          {!isFree && (
            <div className="flex flex-col gap-2 body_medium_m text-content_primary">
              {data?.product?.price ? (
                <p>
                  ${data?.product?.price} {payCycleText}
                </p>
              ) : null}
              {renewalDate && !isLifetime ? (
                <p>
                  <span className="text-content_tertiary">
                    {data?.auto_renewal
                      ? I18n.t("Renewal date:")
                      : I18n.t("Expiry Date:")}{" "}
                  </span>
                  {renewalDate}
                </p>
              ) : null}
              <p>
                <span className="text-content_tertiary">
                  {I18n.t("Automatic renewal:")}{" "}
                </span>
                {autoRenewalText}
              </p>
            </div>
          )}
        </div>

        <div className="h-px bg-border_white_transparent10" />

        <div className="flex flex-col gap-4">
          <h3 className="title_xl text-content_primary">
            {I18n.t("Included in your plan")}
          </h3>
          <div className="flex flex-col gap-4">
            {features.map((feature) => (
              <FeatureRow key={feature} text={feature} />
            ))}
          </div>
        </div>
      </div>
    );
  },
);

SubscriptionPlanCard.displayName = "SubscriptionPlanCard";
